'use client';

import { useEffect } from 'react';

type ErrorPageProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

function isNetworkError(error: Error) {
  const message = error.message.toLowerCase();
  return (
    error.name === 'AulaNetworkError' ||
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('network request failed') ||
    message.includes('fetch failed') ||
    message.includes('load failed')
  );
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  useEffect(() => {
    console.error('[Aula] Route error', error);
    void fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        kind: 'route-error-boundary',
        name: error.name,
        message: error.message,
        digest: error.digest ?? null,
        path: window.location.pathname,
      }),
      keepalive: true,
    }).catch(() => {});
  }, [error]);

  const network = isNetworkError(error);

  return (
    <main className="flex min-h-[70vh] items-center justify-center px-4 py-10">
      <section className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-xl sm:p-8">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-2xl" aria-hidden="true">
          {network ? '↻' : '!'}
        </div>
        <h1 className="mt-4 text-xl font-bold text-slate-950">
          {network ? "We couldn't connect." : 'Something went wrong.'}
        </h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          {network
            ? 'Your internet connection may have been interrupted. Reload the page and try again.'
            : 'Aula ran into a problem loading this page. Try again, or reload the page if the problem continues.'}
        </p>
        <div className="mt-6 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={reset}
            className="min-h-11 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="min-h-11 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Reload page
          </button>
        </div>
      </section>
    </main>
  );
}
